import React from 'react';
import {Box, Grid, Typography} from '@mui/material';
import {PieChart, Pie, Cell, ResponsiveContainer, Tooltip} from 'recharts';
import PartnerDashboardShell from '../components/PartnerDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, PageHeader, BandChip} from '../components/widgets.comp';
import {READINESS_BAND_COLORS, CONTEXT_BAND_COLORS, RISK_BAND_COLORS} from '../constants';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, cellText, anyPreview, movementSide, movementDelta, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';

const BLUE = '#3B6FA0';
const GREY = '#D1D5DB';
const DARK = '#1F2937';

// The 4-tier "X Risk" labels (Cyber Risk Experience) map positionally onto
// the same Monitor/Watch/Elevated/Priority Concern rubric used everywhere
// else — see RUBRIC_GROUPS in OverviewSections.comp.jsx.
const RISK_EXPERIENCE_COLORS = {
    'No or Minimal Risk': RISK_BAND_COLORS.Monitor,
    'Low Risk': RISK_BAND_COLORS.Watch,
    'Moderate Risk': RISK_BAND_COLORS.Elevated,
    'High Risk': RISK_BAND_COLORS['Priority Concern'],
};

const rubricColor = (label) =>
    READINESS_BAND_COLORS[label] || CONTEXT_BAND_COLORS[label] || RISK_EXPERIENCE_COLORS[label] || RISK_BAND_COLORS[label] || '#6B7280';

/* Slide 28 — Child Online Safety (COS) Reassessment Overview. Live data
   (Data Map PD-28-02 … 07):
     score cards   PD.S_19.C_2 Readiness · C_3 Any Cyber Risk · C_4 Context & Support (MovementCard)
     duration C_5 · completion donut C_6 · Matched Cohort C_7 · completion by country → school C_8
   Each score card shows the baseline year and the reassessment year side by
   side, each with its own Global avg (PD slide 51). Until the portfolio
   reassesses, the cards are preview_only: the reassessment side reads "—",
   the delta "Pending", and the preview banner shows. */
const THRESHOLD_PCT = 50;
const numeric = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const whole = (v) => Math.trunc(Number(v));

const toScore = (title, card) => {
    const base = movementSide(card, 'baseline');
    const re = movementSide(card, 'reassess');
    return {
        title,
        y2025Label: base.label, y2025: base.value, band2025: base.band, globalAvg2025: base.global,
        y2026Label: re.label, y2026: re.value, band2026: re.band, globalAvg2026: re.global,
        delta: movementDelta(card),
        suppressed: base.suppressed || re.suppressed,       // a side under the n floor: "—" and the reason
    };
};

const PreviewBanner = () => (
    <Box sx={{
        mb: 3, p: {xs: 2, md: 2.5}, borderRadius: '0 8px 8px 0',
        backgroundColor: 'var(--brand-surface-subtle, #F3F4F6)', border: '1px solid var(--brand-border, #E5E7EB)',
        borderLeft: `4px solid ${NAVY}`,
    }}>
        <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, mb: 0.5}}>
            Preview only: Your country has not completed reassessment yet.
        </Typography>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>
            The figures below show how the confirmed COS Movement view will read once reassessment is complete. We recommend reassessing about one year after the baseline assessment to see how far children have progressed in readiness, cyber risk experience, and the context and support around them.
        </Typography>
    </Box>
);

const CompositeScoreCard = ({title, y2025Label, y2025, band2025, y2026Label, y2026, band2026, delta, globalAvg2025, globalAvg2026, suppressed}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, height: '100%',
    }}>
        <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: suppressed ? 0.5 : 2}}>{title}</Typography>
        {suppressed && <Typography sx={{fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', mb: 1.5}}>{SUPPRESSED_COPY}</Typography>}

        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5}}>
            <Box sx={{textAlign: 'center'}}>
                <Typography sx={{fontSize: '11px', color: '#9CA3AF', mb: 0.5}}>{y2025Label}</Typography>
                <Typography sx={{fontSize: '22px', fontWeight: 800, color: '#6A7282'}}>{y2025}</Typography>
                {band2025 && <Box sx={{mt: 0.5}}><BandChip label={band2025} color={rubricColor(band2025)}/></Box>}
                <Typography sx={{fontSize: '11px', color: '#9CA3AF', mt: 0.75}}>Global Avg. {globalAvg2025}</Typography>
            </Box>
            <Typography sx={{fontSize: '20px', color: '#CBD5E1'}}>&rarr;</Typography>
            <Box sx={{textAlign: 'center'}}>
                <Typography sx={{fontSize: '11px', color: '#9CA3AF', mb: 0.5}}>{y2026Label}</Typography>
                <Typography sx={{fontSize: '22px', fontWeight: 800, color: NAVY}}>{y2026}</Typography>
                {band2026 && <Box sx={{mt: 0.5}}><BandChip label={band2026} color={rubricColor(band2026)}/></Box>}
                <Typography sx={{fontSize: '11px', color: '#9CA3AF', mt: 0.75}}>Global Avg. {globalAvg2026}</Typography>
            </Box>
            <Box sx={{
                fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap',
                color: delta.startsWith('+') ? '#1B6B55' : (delta.startsWith('-') ? '#E04535' : '#9CA3AF'),
            }}>
                {delta}
            </Box>
        </Box>
    </Box>
);

const ReassessmentDurationBox = ({card}) => {
    // "2026-09-16 – 2026-10-16" → Start / End, as the slide lays it out; otherwise as given ("Pending").
    const text = cellText(card?.kpi_value);
    const parts = text.split(/\s+[–-]\s+/);
    return (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, mb: 3,
    }}>
        <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
            Reassessment Duration
        </Typography>
        <Typography sx={{fontSize: '14px', color: '#374151'}}>
            {parts.length === 2
                ? <><strong>Start Date:</strong> {parts[0]} &nbsp;&nbsp;-&nbsp;&nbsp; <strong>End Date:</strong> {parts[1]}</>
                : text}
        </Typography>
    </Box>
    );
};

const CompletionDonut = ({completion, cohort}) => {
    const rate = numeric(completion?.kpi_value) ? Math.min(100, Math.max(0, whole(completion.kpi_value))) : null;
    const info = (name) => (completion?.kpi_add_infos || []).find((i) => String(i?.info_name || '').toLowerCase() === name)?.info_value;
    const assessed = numeric(info('assessed')) ? whole(info('assessed')) : null;
    const notAssessed = numeric(info('not assessed')) ? whole(info('not assessed')) : null;
    const donut = rate === null ? [] : [
        {name: 'Assessed', value: assessed !== null ? assessed : rate},
        {name: 'Not Assessed', value: notAssessed !== null ? notAssessed : 100 - rate},
    ];
    const cohortText = numeric(cohort?.kpi_value) ? `${whole(cohort.kpi_value)}%` : cellText(cohort?.kpi_value);
    return (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, height: '100%',
    }}>
        <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
            Total Assessment Completion Rate
        </Typography>
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            {rate === null ? (
                <Typography sx={{fontSize: '26px', fontWeight: 800, color: NAVY, my: 3}}>{cellText(completion?.kpi_value)}</Typography>
            ) : (<>
            <Box sx={{width: '100%', maxWidth: 220, height: 180}}>
                <ResponsiveContainer>
                    <PieChart>
                        <Pie data={donut} dataKey="value" innerRadius={50} outerRadius={72} paddingAngle={1}>
                            <Cell fill={BLUE}/>
                            <Cell fill={GREY}/>
                        </Pie>
                        <Tooltip/>
                    </PieChart>
                </ResponsiveContainer>
            </Box>
            <Box sx={{display: 'flex', gap: 3, mt: 1}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                    <Box sx={{width: 10, height: 10, borderRadius: '2px', backgroundColor: BLUE}}/>
                    <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Assessed ({donut[0].value})</Typography>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                    <Box sx={{width: 10, height: 10, borderRadius: '2px', backgroundColor: GREY}}/>
                    <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Not Assessed ({donut[1].value})</Typography>
                </Box>
            </Box>
            </>)}
            <Typography sx={{fontSize: '13px', color: '#374151', mt: 2}}>
                <strong>Matched Cohort:</strong> {cohortText}
            </Typography>
        </Box>
    </Box>
    );
};

const CompletionBarRow = ({label, indent, pct, fraction, status}) => {
    const met = status === 'Met';
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.75}}>
            <Typography sx={{
                fontSize: '13px', fontWeight: indent ? 500 : 700, color: indent ? '#374151' : NAVY,
                width: 90, flexShrink: 0, pl: indent ? 1.5 : 0,
            }}>
                {label}
            </Typography>
            <Box sx={{position: 'relative', flex: 1, height: 14, borderRadius: '7px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)'}}>
                <Box sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`,
                    backgroundColor: met ? BLUE : DARK, borderRadius: '7px',
                }}/>
                <Box sx={{
                    position: 'absolute', left: `${THRESHOLD_PCT}%`, top: -3, bottom: -3, width: '2px',
                    backgroundColor: NAVY,
                }}/>
            </Box>
            <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, width: 90, flexShrink: 0, textAlign: 'right'}}>
                {pct}%{fraction ? ` (${fraction})` : ''}
            </Typography>
            <Box sx={{width: 90, flexShrink: 0, display: 'flex', justifyContent: 'flex-end'}}>
                {status && <BandChip label={status} color={met ? '#1B6B55' : DARK}/>}
            </Box>
        </Box>
    );
};

const CompletionByCountrySchool = ({rows}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, height: '100%',
    }}>
        <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 2}}>
            Reassessment Completion Rate by School (50% Threshold)
        </Typography>
        {rows.length === 0 && (
            <Typography sx={{fontSize: '13.5px', color: '#6A7282', py: 3, textAlign: 'center'}}>Pending — shown once reassessment starts.</Typography>
        )}
        {rows.map((row, i) => (
            <CompletionBarRow key={`${row.label}-${i}`} {...row}/>
        ))}
    </Box>
);

const REASSESSMENT_SECTION_CODE = 'S_19';
const CARD_SECTIONS = [REASSESSMENT_SECTION_CODE];

const ReassessmentPage = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const card = (c) => findCard(dashboardData, REASSESSMENT_SECTION_CODE, c);
    const scores = [
        toScore('COS Composite Score', card('C_2')),
        toScore('Cyber Risk Experience', card('C_3')),
        toScore('Context & Support', card('C_4')),
    ];
    const preview = anyPreview(...['C_2', 'C_3', 'C_4', 'C_5', 'C_6', 'C_7', 'C_8'].map(card));

    // C_8 columns: Unit · Type · Assessed · Registered · Rate % · Status. Schools indent under their country.
    const cols = card('C_8')?.table_objects || [];
    const col = (name) => cols.find((c) => String(c.column_name || '').trim().toLowerCase() === name);
    const unit = col('unit'), type = col('type'), assessed = col('assessed'), registered = col('registered');
    const rate = col('rate %') || col('rate'), status = col('status');
    const hasParents = (type?.values || []).some((t) => /country|region/i.test(String(t)));
    const completionRows = (unit?.values || []).map((label, i) => ({
        label,
        indent: hasParents && /school/i.test(String(type?.values?.[i] || '')),
        pct: numeric(rate?.values?.[i]) ? whole(rate.values[i]) : 0,
        fraction: numeric(assessed?.values?.[i]) && numeric(registered?.values?.[i]) ? `${whole(assessed.values[i])}/${whole(registered.values[i])}` : '',
        status: status?.values?.[i] || '',
    }));

    if (loading) {
        return (
            <PartnerDashboardShell>
                <CircularLoader />
            </PartnerDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <PartnerDashboardShell>
                <NoDataAvailable />
            </PartnerDashboardShell>
        );
    }

    return (
    <PartnerDashboardShell>
        <PageHeader
            title="Child Online Safety (COS) Reassessment Overview"
            subtitle="How far have children progressed since the baseline?"
        />

        {preview && <PreviewBanner/>}

        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            Assessment Scores
        </Typography>
        <Grid container spacing={2.5} alignItems="stretch" sx={{mb: 4}}>
            {scores.map((s) => (
                <Grid item xs={12} md={4} key={s.title}>
                    <CompositeScoreCard {...s}/>
                </Grid>
            ))}
        </Grid>

        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            Completion Status
        </Typography>
        <ReassessmentDurationBox card={card('C_5')}/>
        <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} md={5}>
                <CompletionDonut completion={card('C_6')} cohort={card('C_7')}/>
            </Grid>
            <Grid item xs={12} md={7}>
                <CompletionByCountrySchool rows={completionRows}/>
            </Grid>
        </Grid>
    </PartnerDashboardShell>
    );
};

export default ReassessmentPage;
